import { TableV3, TableV3Ref, hasTableV3Filters } from 'common/table';
import { CANDIDATE_PROFILE_TABLE_ID } from 'modules/candidate-profile/constants';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { useCandidateProfileTableLogic } from 'modules/candidate-profile/tables/hooks/useCandidateProfileTableLogic';
import { CandidateExplanationsContext } from 'modules/candidate-profile/tables/CandidateExplanationsContext';
import { useCandidateProfileSelectionBar } from 'modules/candidate-profile/hooks/useCandidateProfileSelectionBar';
import { TableV3Filters } from 'common/table/components/TableV3';
import { SearchCandidateVectorCandidateProfile } from 'modules/candidate-profile/types/SearchCandidateVectorCandidateProfile';
import { SearchCandidateVectorQueryMessage } from 'modules/candidate-profile/types/SearchCandidateVectorQueryMessage';
import { useSearchCandidateProfileSelected } from 'modules/candidate-profile/hooks/useSearchCandidateProfileSelected';
import { Box } from '@mui/material';
import { candidateProfileTableSx } from 'modules/candidate-profile/styles/candidateProfileTableSx';
import { CandidateProfileListProps } from 'modules/candidate-profile/types/CandidateProfileListProps';
import { CandidateProfileEmptyState } from 'modules/candidate-profile/tables/states/CandidateProfileEmptyState';
import { withFilterModeHeaders } from 'modules/candidate-profile/tables/filters/withFilterModeHeaders';
import { LEGACY_FILTER_KEYS } from 'modules/candidate-profile/tables/filters/filterFieldAliases';
import {
  CandidateFilterGroups,
  CandidateFilterState,
  ExplanationContext,
  SourcingFilterField
} from 'modules/candidate-profile/types/CandidateSearchFilter';

type CandidateProfileTableProps = {
  candidateProfileListProps: CandidateProfileListProps;
  tableRef: React.RefObject<TableV3Ref<CandidateProfileTableColumns>>;
  filters: TableV3Filters<CandidateProfileTableColumns> | undefined;
  filterConditions: CandidateFilterState[];
  filterGroups: CandidateFilterGroups;
  candidateProfiles: SearchCandidateVectorCandidateProfile[];
  explanationContext: ExplanationContext | null;
  /** False until the first search returns, even one with no rows. */
  hasSearchResult: boolean;
  isVectorSearchInitialized: boolean;
  isSearchLoading: boolean;
  conversationProps: {
    isConversationLoading: boolean;
    placeholderMessage: SearchCandidateVectorQueryMessage | null;
  } | null;
  onChangeFilters: (
    filters: TableV3Filters<CandidateProfileTableColumns>
  ) => void;
  onRefetchVectorSearch: () => Promise<{ data: unknown }>;
  clearSelectionRef?: React.MutableRefObject<(() => void) | null>;
  filterModeOptions?: {
    availableFilters: SourcingFilterField[];
    renderHeader: (field: string) => React.ReactNode;
  };
};

export const CandidateProfileTable: React.FC<CandidateProfileTableProps> = ({
  candidateProfileListProps,
  tableRef,
  filters,
  filterConditions,
  filterGroups,
  candidateProfiles,
  explanationContext,
  hasSearchResult,
  isVectorSearchInitialized,
  isSearchLoading,
  conversationProps,
  onChangeFilters,
  onRefetchVectorSearch,
  filterModeOptions,
  clearSelectionRef
}) => {
  const {
    sessionId,
    isInitialized,
    isCreatingSession,
    isSavingSelection,
    selectedCandidateIds,
    handleSelectionChange
  } = useSearchCandidateProfileSelected({
    tableRef,
    sessionId: undefined
  });

  if (clearSelectionRef) {
    clearSelectionRef.current = () => {
      tableRef.current?.deselectAll();
      handleSelectionChange([]);
    };
  }

  const {
    paginatedData,
    explanations,
    columns,
    currentPage,
    pageSize,
    sort,
    handleSort,
    mappedCandidateProfiles,
    isDuplicateCheckLoading,
    integrationProvider,
    handleChangePage,
    handleChangePageSize,
    handleDeselectAll,
    handleInviteToApply,
    handleAddToTalentList,
    handleAddToWorkflow,
    handleRemoveSelectedFromTalentList,
    handleAddToJob,
    handleAddToJobAction,
    hasAddToJobAction,
    isAddToJobActionOverLimit,
    isAtsConnected,
    isFromWorkflowBuilder,
    isWorkflowBuilderEnabled
  } = useCandidateProfileTableLogic({
    candidateProfileListProps,
    tableRef,
    filters,
    filterConditions,
    filterGroups,
    sessionId,
    candidateProfiles,
    explanationContext,
    hasSearchResult,
    isConversationLoading: conversationProps?.isConversationLoading ?? false,
    chatTurnId: conversationProps?.placeholderMessage?.id ?? null,
    selectedCandidateIds,
    onRefetchVectorSearch
  });

  const columnsWithFilterModes = filterModeOptions
    ? withFilterModeHeaders({
        columns,
        availableFilters: filterModeOptions.availableFilters,
        renderHeader: filterModeOptions.renderHeader
      })
    : columns;

  useCandidateProfileSelectionBar({
    candidateProfileListProps,
    selectedCandidateIds,
    onDeselectAll: handleDeselectAll,
    onInviteToApply: handleInviteToApply,
    onAddToTalentList: handleAddToTalentList,
    onAddToWorkflow: handleAddToWorkflow,
    onRemoveFromTalentList: handleRemoveSelectedFromTalentList,
    onAddToJob: handleAddToJob,
    onAddToJobAction: handleAddToJobAction,
    hasAddToJobAction,
    isAddToJobActionOverLimit,
    isAtsConnected,
    isFromWorkflowBuilder,
    isWorkflowBuilderEnabled
  });

  return (
    <CandidateExplanationsContext.Provider value={explanations}>
      {(isCreatingSession || isSavingSelection) && (
        <Box sx={candidateProfileTableSx.loadingOverlay} />
      )}
      <TableV3
        ref={tableRef}
        tableId={CANDIDATE_PROFILE_TABLE_ID}
        persistColumnState
        columns={columnsWithFilterModes}
        hideFilterSummary={Boolean(filterModeOptions)}
        columnsCacheKey={sessionId ?? undefined}
        filterKeyAliases={LEGACY_FILTER_KEYS}
        data={paginatedData}
        selectionOptions={{
          mode: 'multiRow',
          identifier: 'id',
          onSelectionChanged: handleSelectionChange
        }}
        onFiltersChanged={onChangeFilters}
        onSort={(nextSort) =>
          handleSort({
            field: nextSort.field,
            direction: nextSort.direction
          })
        }
        defaultSort={
          sort ? { field: sort.field, direction: sort.direction } : 'unset'
        }
        loading={
          isSearchLoading ||
          conversationProps?.isConversationLoading ||
          !isInitialized ||
          !isVectorSearchInitialized ||
          isDuplicateCheckLoading
        }
        emptyStateComponent={
          <CandidateProfileEmptyState
            candidateProfileListProps={candidateProfileListProps}
            integrationProvider={integrationProvider}
            isFiltering={hasTableV3Filters(filters)}
          />
        }
        paginationOptions={{
          pageSize,
          totalRows: mappedCandidateProfiles.length,
          currentPage,
          onChangePage: handleChangePage,
          onChangePageSize: handleChangePageSize
        }}
      />
    </CandidateExplanationsContext.Provider>
  );
};
